import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { postSizeEnumToDisplay } from '../order/building-constants';
import { useGetProductsReportQuery } from '../order/orders-api-slice';
import { selectCurrentUser } from '../user';
import {
  ManufacturerFilter,
  ReportLayout,
  ReportTablePagination,
  SortableHeadCell,
  useSortableTable,
} from './components';
import {
  formatProductsReportSize,
  getCategoryAxisWidth,
  getCategoryTickMaxLength,
  getProductsReportBarChartMargin,
  truncateCategoryLabel,
} from './report-chart-config';
import type { ProductSummary } from '../../types';

const TRUSS_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'Gable', label: 'Gable' },
  { value: 'LeanTo', label: 'Lean-To' },
  { value: 'Header', label: 'Header' },
];

const TRUSS_TYPE_DISPLAY: Record<string, string> = {
  Gable: 'Gable',
  LeanTo: 'Lean-To',
  Header: 'Header',
};

const PRODUCTS_SORT_ACCESSORS: Record<string, (product: ProductSummary) => string | number | null> =
  {
    totalQuantity: p => p.totalQuantity,
    trussSizeFt: p => p.trussSizeFt * 12 + (p.trussSizeInches ?? 0),
    pitch: p => p.pitch,
    postSize: p => postSizeEnumToDisplay[p.postSize] ?? p.postSize,
    trussType: p => TRUSS_TYPE_DISPLAY[p.trussType] ?? p.trussType,
    overhang: p => p.overhang,
    overhangCustom: p => p.overhangCustom,
  };

interface ProductsReportChartTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: { label?: string; quantity?: number } }>;
}

function ProductsReportChartTooltip({ active, payload }: ProductsReportChartTooltipProps) {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload as { label?: string; quantity?: number } | undefined;
  if (!row?.label) {
    return null;
  }
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        maxWidth: 360,
      }}
    >
      <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
        {row.label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Qty: {(row.quantity ?? 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

export default function ProductsReport() {
  const currentUser = useAppSelector(selectCurrentUser);

  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;

  // View mode toggle — table (default) or graph
  const [viewMode, setViewMode] = useState<'table' | 'graph'>('table');

  // Filter state (form values — not yet submitted)
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    undefined
  );
  const [trussType, setTrussType] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);

  // Submitted state (drives the query)
  const [submittedManufacturerId, setSubmittedManufacturerId] = useState<number | undefined>(
    undefined
  );
  const [submittedTrussType, setSubmittedTrussType] = useState('');
  const [submittedOrderDateFrom, setSubmittedOrderDateFrom] = useState('');
  const [submittedOrderDateTo, setSubmittedOrderDateTo] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Resolve effective manufacturer: admin uses selected; others use their own
  const effectiveManufacturerId = isAdmin ? submittedManufacturerId : currentUser?.manufacturerId;

  const {
    data: reportData,
    isLoading,
    error,
  } = useGetProductsReportQuery(
    {
      manufacturerId: effectiveManufacturerId,
      trussType: submittedTrussType || undefined,
      orderDateFrom: submittedOrderDateFrom || undefined,
      orderDateTo: submittedOrderDateTo || undefined,
    },
    {
      skip:
        !hasSearched ||
        (!isAdmin && !currentUser?.manufacturerId) ||
        (isAdmin && !submittedManufacturerId),
    }
  );

  const handleManufacturerChange = useCallback((mfgId: number | undefined) => {
    setSelectedManufacturerId(mfgId);
  }, []);

  const handleDateFromChange = useCallback(
    (value: string) => {
      setOrderDateFrom(value);
      if (value && orderDateTo && value > orderDateTo) {
        setDateError('From date must be on or before To date');
      } else {
        setDateError(null);
      }
    },
    [orderDateTo]
  );

  const handleDateToChange = useCallback(
    (value: string) => {
      setOrderDateTo(value);
      if (orderDateFrom && value && orderDateFrom > value) {
        setDateError('To date must be on or after From date');
      } else {
        setDateError(null);
      }
    },
    [orderDateFrom]
  );

  const handleSearch = useCallback(() => {
    if (orderDateFrom && orderDateTo && orderDateFrom > orderDateTo) {
      setDateError('From date must be on or before To date');
      return;
    }
    setDateError(null);
    setSubmittedManufacturerId(selectedManufacturerId);
    setSubmittedTrussType(trussType);
    setSubmittedOrderDateFrom(orderDateFrom);
    setSubmittedOrderDateTo(orderDateTo);
    setHasSearched(true);
  }, [selectedManufacturerId, trussType, orderDateFrom, orderDateTo]);

  const handleClear = useCallback(() => {
    setTrussType('');
    setOrderDateFrom('');
    setOrderDateTo('');
    setDateError(null);
    setSubmittedTrussType('');
    setSubmittedOrderDateFrom('');
    setSubmittedOrderDateTo('');
    // Re-submit with cleared filters (keep manufacturer)
    setSubmittedManufacturerId(selectedManufacturerId);
    setHasSearched(true);
  }, [selectedManufacturerId]);

  const needsManufacturer = isAdmin && hasSearched && !submittedManufacturerId;

  // Build chart data from top 20 products (sorted desc by totalQuantity from API)
  const chartData = useMemo(() => {
    if (!reportData?.products) return [];
    return reportData.products.slice(0, 20).map(p => ({
      label: `${formatProductsReportSize(p.trussSizeFt, p.trussSizeInches)} / ${p.pitch ?? '—'} / ${TRUSS_TYPE_DISPLAY[p.trussType] ?? p.trussType}`,
      quantity: p.totalQuantity,
    }));
  }, [reportData]);

  const categoryAxisWidth = useMemo(
    () => getCategoryAxisWidth(chartData.map(row => row.label)),
    [chartData]
  );

  const { pageRows, page, rowsPerPage, getSortProps, handleChangePage, handleChangeRowsPerPage } =
    useSortableTable<ProductSummary>({
      data: reportData?.products ?? [],
      accessors: PRODUCTS_SORT_ACCESSORS,
      initialSortField: 'totalQuantity',
      initialSortDirection: 'desc',
    });

  const categoryTickMaxLength = useMemo(
    () => getCategoryTickMaxLength(categoryAxisWidth),
    [categoryAxisWidth]
  );

  const chartMargin = useMemo(
    () => getProductsReportBarChartMargin(categoryAxisWidth),
    [categoryAxisWidth]
  );

  return (
    <ReportLayout
      title="Products Report"
      description="View aggregated truss product data grouped by specifications. Sort by total quantity to identify highest-volume products."
      isLoading={false}
      error={null}
      filters={
        <>
          <ManufacturerFilter
            isAdmin={isAdmin}
            selectedManufacturerId={selectedManufacturerId}
            onManufacturerChange={handleManufacturerChange}
          />
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel id="truss-type-label" shrink>
              Truss Type
            </InputLabel>
            <Select
              labelId="truss-type-label"
              value={trussType}
              label="Truss Type"
              displayEmpty
              notched
              onChange={e => setTrussType(e.target.value)}
              renderValue={selected => {
                if (selected === '') return 'All Types';
                const opt = TRUSS_TYPE_OPTIONS.find(o => o.value === selected);
                return opt?.label ?? '';
              }}
            >
              {TRUSS_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Date From"
            type="date"
            value={orderDateFrom}
            onChange={e => handleDateFromChange(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
            error={dateError?.includes('From') ?? false}
            helperText={dateError?.includes('From') ? dateError : undefined}
          />
          <TextField
            label="Date To"
            type="date"
            value={orderDateTo}
            onChange={e => handleDateToChange(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
            error={dateError?.includes('To') ?? false}
            helperText={dateError?.includes('To') ? dateError : undefined}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={isLoading}
            sx={{ height: 40 }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClear}
            disabled={isLoading}
            sx={{ height: 40 }}
          >
            Clear
          </Button>
        </>
      }
    >
      {!hasSearched ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">
            {isAdmin
              ? 'Select a manufacturer and click Search to view the report.'
              : 'Click Search to view the Products Report.'}
          </Typography>
        </Box>
      ) : needsManufacturer ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">
            Please select a Manufacturer to view the report.
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading report...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load report data. Please try again later.</Alert>
      ) : reportData ? (
        <>
          {/* Table / Graph toggle — only shown when results exist */}
          {reportData.products.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => {
                  if (v) setViewMode(v);
                }}
                size="small"
              >
                <ToggleButton value="table">Table</ToggleButton>
                <ToggleButton value="graph">Graph</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Card>
            <CardContent>
              {reportData.products.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No products found for the selected criteria.
                </Typography>
              ) : viewMode === 'table' ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            align="right"
                            {...getSortProps('totalQuantity')}
                          >
                            Total Qty
                          </SortableHeadCell>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            align="right"
                            {...getSortProps('trussSizeFt')}
                          >
                            Size
                          </SortableHeadCell>
                          <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('pitch')}>
                            Pitch
                          </SortableHeadCell>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            {...getSortProps('postSize')}
                          >
                            Post Size
                          </SortableHeadCell>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            {...getSortProps('trussType')}
                          >
                            Type
                          </SortableHeadCell>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            {...getSortProps('overhang')}
                          >
                            Overhang
                          </SortableHeadCell>
                          <SortableHeadCell
                            sx={{ fontWeight: 'bold' }}
                            align="right"
                            {...getSortProps('overhangCustom')}
                          >
                            Overhang Custom (in)
                          </SortableHeadCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageRows.map(product => (
                          <TableRow
                            key={`${product.trussSizeFt}-${product.trussSizeInches}-${product.pitch}-${product.postSize}-${product.trussType}-${product.overhang}-${product.overhangCustom}`}
                            hover
                          >
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600, color: 'primary.main' }}
                            >
                              {product.totalQuantity.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              {formatProductsReportSize(
                                product.trussSizeFt,
                                product.trussSizeInches
                              )}
                            </TableCell>
                            <TableCell>{product.pitch ?? '—'}</TableCell>
                            <TableCell>
                              {postSizeEnumToDisplay[product.postSize] ?? product.postSize}
                            </TableCell>
                            <TableCell>
                              {TRUSS_TYPE_DISPLAY[product.trussType] ?? product.trussType}
                            </TableCell>
                            <TableCell>{product.overhang ?? '—'}</TableCell>
                            <TableCell align="right">
                              {product.overhangCustom != null ? product.overhangCustom : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <ReportTablePagination
                      count={reportData.products.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableContainer>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {reportData.products.length} product
                    {reportData.products.length !== 1 ? 's' : ''} found — click any column header to
                    sort
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Top {chartData.length} products by total quantity
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Hover a bar for the full product description
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 40)}>
                    <BarChart data={chartData} layout="vertical" margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        label={{ value: 'Total Quantity', position: 'bottom', offset: 8 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={categoryAxisWidth}
                        tick={{ fontSize: 11, textAnchor: 'end' }}
                        tickFormatter={value =>
                          truncateCategoryLabel(String(value), categoryTickMaxLength)
                        }
                      />
                      <Tooltip content={<ProductsReportChartTooltip />} />
                      <Bar dataKey="quantity" fill="#1976d2" name="Total Quantity" />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </ReportLayout>
  );
}
